/**
 * The arcade classics: Snake, Pong and Flappy.
 *
 * These three are the reason the shared kit exists. Each runs a real loop with
 * real state, and each needs to work with a keyboard, a mouse and a thumb. The
 * rule throughout is that the simulation advances on elapsed TIME, never on
 * frames: a 120Hz phone would otherwise play Snake at double speed.
 */
import { byLevel } from '@arena/games';

import { Lifecycle, dpad, fitCanvas, h, tone } from '../kit.js';
import type { GameDef } from '../types.js';

function pill(label: string, value: string): [HTMLElement, HTMLElement] {
  const box = h('div', 'ac-pill');
  const strong = h('strong', '', value);
  box.append(h('span', '', label), strong);
  return [box, strong];
}

// --------------------------------------------------------------------- snake

export const snake: GameDef = {
  id: 'snake',
  title: 'Snake',
  blurb: 'Mange, grandis, evite ta propre queue.',
  category: 'Arcade',
  difficulty: 'Moyen',
  icon: '\u{1F40D}',
  unit: 'points',
  higherIsBetter: true,
  how: 'Fleches, ZQSD ou glisse du doigt. Chaque pomme allonge le serpent et accelere la partie.',
  mount(root, ctx) {
    const life = new Lifecycle();
    const GRID = 20;

    /*
     * Difficulty is the pace, and whether the wall is fatal.
     *
     * The wrap on easy is the big one: nearly every early death is a wall, not
     * the tail, so removing the wall turns the game from "you lasted four
     * seconds" into one you actually get to learn. The pace knobs are the
     * starting step, how sharply each apple speeds it up, and the floor it can
     * never go below, which is what stops hard mode becoming a reflex lottery.
     */
    const START = byLevel(ctx.level, 0.19, 0.14, 0.11);
    const FLOOR = byLevel(ctx.level, 0.11, 0.06, 0.045);
    const RAMP = byLevel(ctx.level, 0.975, 0.96, 0.945);
    const WRAPS = ctx.level === 'facile';

    const [scoreBox, scoreValue] = pill('Score', '0');
    const [lenBox, lenValue] = pill('Longueur', '3');
    const hud = h('div', 'ac-hud');
    hud.append(scoreBox, lenBox);

    const canvas = h('canvas', 'ac-canvas') as HTMLCanvasElement;
    const { w, h: hh, ctx: g } = fitCanvas(canvas, life);

    let body = [{ x: 10, y: 10 }];
    let dir = { x: 1, y: 0 };
    /**
     * The direction the next step will use.
     *
     * Separate from `dir` on purpose. Reading input straight into `dir` lets two
     * quick turns inside one step reverse the snake into itself, which reads as
     * the game killing you for playing well.
     */
    let queued = { x: 1, y: 0 };
    let food = { x: 15, y: 10 };
    let score = 0;
    let alive = true;
    let acc = 0;
    let stepTime = START;

    const placeFood = (): void => {
      let spot: { x: number; y: number };
      do {
        spot = { x: Math.floor(Math.random() * GRID), y: Math.floor(Math.random() * GRID) };
      } while (body.some((s) => s.x === spot.x && s.y === spot.y));
      food = spot;
    };

    const reset = (): void => {
      body = [
        { x: 10, y: 10 },
        { x: 9, y: 10 },
        { x: 8, y: 10 },
      ];
      dir = { x: 1, y: 0 };
      queued = { x: 1, y: 0 };
      score = 0;
      stepTime = START;
      alive = true;
      scoreValue.textContent = '0';
      lenValue.textContent = '3';
      placeFood();
    };

    const turn = (x: number, y: number): void => {
      // Refuse a reversal, measured against the direction actually in effect.
      if (dir.x === -x && dir.y === -y) return;
      queued = { x, y };
    };

    const step = (): void => {
      dir = queued;
      const head = { x: body[0].x + dir.x, y: body[0].y + dir.y };
      if (WRAPS) {
        // Off one edge, on at the other. Only your own tail can kill you.
        head.x = (head.x + GRID) % GRID;
        head.y = (head.y + GRID) % GRID;
      }
      if (
        head.x < 0 ||
        head.y < 0 ||
        head.x >= GRID ||
        head.y >= GRID ||
        body.some((s) => s.x === head.x && s.y === head.y)
      ) {
        alive = false;
        tone(150, 260, 'square');
        ctx.submit(score);
        return;
      }
      body.unshift(head);
      if (head.x === food.x && head.y === food.y) {
        score += 10;
        scoreValue.textContent = String(score);
        // Speed up, but with a floor, or it becomes unplayable rather than hard.
        stepTime = Math.max(FLOOR, stepTime * RAMP);
        tone(700, 50, 'triangle');
        placeFood();
      } else {
        body.pop();
      }
      lenValue.textContent = String(body.length);
    };

    life.on(window, 'keydown', (ev: KeyboardEvent) => {
      const k = ev.key.toLowerCase();
      if (k === 'arrowup' || k === 'z' || k === 'w') turn(0, -1);
      else if (k === 'arrowdown' || k === 's') turn(0, 1);
      else if (k === 'arrowleft' || k === 'q' || k === 'a') turn(-1, 0);
      else if (k === 'arrowright' || k === 'd') turn(1, 0);
      else if (!alive && k === ' ') reset();
      else return;
      ev.preventDefault();
    });

    // Swipe, with a threshold so a tap is not read as a tiny flick.
    let touchStart: { x: number; y: number } | null = null;
    life.on(canvas, 'pointerdown', (ev: Event) => {
      const e = ev as PointerEvent;
      e.preventDefault();
      if (!alive) {
        reset();
        return;
      }
      touchStart = { x: e.clientX, y: e.clientY };
    });
    life.on(canvas, 'pointerup', (ev: Event) => {
      const e = ev as PointerEvent;
      if (!touchStart) return;
      const dx = e.clientX - touchStart.x;
      const dy = e.clientY - touchStart.y;
      touchStart = null;
      if (Math.hypot(dx, dy) < 18) return;
      if (Math.abs(dx) > Math.abs(dy)) turn(dx > 0 ? 1 : -1, 0);
      else turn(0, dy > 0 ? 1 : -1);
    });

    life.frame((dt) => {
      if (alive) {
        acc += dt;
        while (acc >= stepTime) {
          acc -= stepTime;
          step();
        }
      }

      const size = Math.min(w(), hh());
      const cell = size / GRID;
      g.fillStyle = '#05030b';
      g.fillRect(0, 0, w(), hh());

      g.strokeStyle = 'rgba(139,92,246,0.13)';
      g.lineWidth = 1;
      for (let i = 1; i < GRID; i++) {
        g.beginPath();
        g.moveTo(i * cell, 0);
        g.lineTo(i * cell, size);
        g.moveTo(0, i * cell);
        g.lineTo(size, i * cell);
        g.stroke();
      }

      g.fillStyle = '#ff2d95';
      g.beginPath();
      g.arc((food.x + 0.5) * cell, (food.y + 0.5) * cell, cell * 0.34, 0, Math.PI * 2);
      g.fill();

      body.forEach((s, i) => {
        g.fillStyle = i === 0 ? '#22d3ee' : `rgba(34,211,238,${Math.max(0.25, 1 - i / 28)})`;
        g.fillRect(s.x * cell + 1.5, s.y * cell + 1.5, cell - 3, cell - 3);
      });

      if (!alive) {
        g.fillStyle = 'rgba(5,3,11,0.82)';
        g.fillRect(0, 0, size, size);
        g.fillStyle = '#f4f0ff';
        g.textAlign = 'center';
        g.font = '800 30px system-ui, sans-serif';
        g.fillText(`${score} points`, size / 2, size / 2 - 8);
        g.font = '500 15px system-ui, sans-serif';
        g.fillStyle = '#a394c8';
        g.fillText('Touche l’ecran ou espace pour rejouer', size / 2, size / 2 + 24);
      }
    });

    reset();
    // Same reason as 2048: a swipe on a touch screen is a guess, and this game
    // asks for one every second or two. A tap on an arrow never misreads.
    const pad = dpad(life, (dx, dy) => {
      if (!alive) {
        reset();
        return;
      }
      turn(dx, dy);
    });
    root.append(
      hud,
      canvas,
      pad,
      h(
        'p',
        'ac-note',
        WRAPS
          ? 'Le serpent traverse les murs. Seule ta queue est fatale.'
          : 'Le serpent accelere a chaque pomme.',
      ),
    );
    return () => life.stop();
  },
};

// ---------------------------------------------------------------------- pong

export const pong: GameDef = {
  id: 'pong',
  title: 'Pong',
  blurb: 'Le classique, contre une IA qui s’accroche.',
  category: 'Arcade',
  difficulty: 'Moyen',
  icon: '\u{1F3D3}',
  unit: 'echanges',
  higherIsBetter: true,
  how: 'Deplace ta raquette a la souris, au doigt, ou avec les fleches. Le score compte les echanges avant de perdre.',
  mount(root, ctx) {
    const life = new Lifecycle();
    const [rallyBox, rallyValue] = pill('Echanges', '0');
    const [scoreBox, scoreValue] = pill('Toi', '0');
    const [aiBox, aiValue] = pill('IA', '0');
    const hud = h('div', 'ac-hud');
    hud.append(rallyBox, scoreBox, aiBox);

    const canvas = h('canvas', 'ac-canvas wide') as HTMLCanvasElement;
    const { w, h: hh, ctx: g } = fitCanvas(canvas, life);

    /*
     * Difficulty is your paddle and the AI's top speed.
     *
     * Both matter, and in opposite ways. The paddle decides how forgiving a
     * return is; the AI's speed cap decides whether a rally can ever end, since
     * an opponent that simply matches the ball never loses. The cap still grows
     * with the rally at every level, so a long rally is always the thing that
     * eventually beats you.
     */
    const PADDLE_H = byLevel(ctx.level, 0.26, 0.18, 0.13);
    const AI_BASE = byLevel(ctx.level, 0.4, 0.55, 0.68);
    const AI_GROWTH = byLevel(ctx.level, 0.014, 0.02, 0.028);
    let you = 0.5;
    let them = 0.5;
    let ball = { x: 0.5, y: 0.5, vx: 0.55, vy: 0.28 };
    let rally = 0;
    let mine = 0;
    let theirs = 0;
    let running = false;
    let keyUp = false;
    let keyDown = false;

    const serve = (toward: number): void => {
      ball = {
        x: 0.5,
        y: 0.5,
        vx: 0.55 * toward,
        vy: (Math.random() - 0.5) * 0.5,
      };
      rally = 0;
      rallyValue.textContent = '0';
    };

    life.on(canvas, 'pointerdown', (ev: Event) => {
      ev.preventDefault();
      if (!running) {
        running = true;
        mine = 0;
        theirs = 0;
        scoreValue.textContent = '0';
        aiValue.textContent = '0';
        serve(1);
      }
    });
    life.on(canvas, 'pointermove', (ev: Event) => {
      const e = ev as PointerEvent;
      const rect = canvas.getBoundingClientRect();
      you = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    });
    life.on(window, 'keydown', (ev: KeyboardEvent) => {
      if (ev.key === 'ArrowUp') keyUp = true;
      if (ev.key === 'ArrowDown') keyDown = true;
      if (!running && ev.key === ' ') {
        running = true;
        serve(1);
      }
    });
    life.on(window, 'keyup', (ev: KeyboardEvent) => {
      if (ev.key === 'ArrowUp') keyUp = false;
      if (ev.key === 'ArrowDown') keyDown = false;
    });

    life.frame((dt) => {
      const W = w();
      const H = hh();

      if (running) {
        if (keyUp) you = Math.max(0, you - dt * 1.4);
        if (keyDown) you = Math.min(1, you + dt * 1.4);

        ball.x += ball.vx * dt;
        ball.y += ball.vy * dt;
        if (ball.y < 0.02) {
          ball.y = 0.02;
          ball.vy = Math.abs(ball.vy);
        }
        if (ball.y > 0.98) {
          ball.y = 0.98;
          ball.vy = -Math.abs(ball.vy);
        }

        // The AI tracks the ball, but with a capped speed, which is what makes
        // it beatable: a paddle that simply matches ball.y never loses.
        const aiSpeed = AI_BASE + Math.min(0.5, rally * AI_GROWTH);
        them += Math.sign(ball.y - them) * Math.min(Math.abs(ball.y - them), aiSpeed * dt);

        const halfPaddle = PADDLE_H / 2;
        if (ball.x < 0.04 && ball.vx < 0) {
          if (Math.abs(ball.y - you) < halfPaddle) {
            ball.vx = Math.abs(ball.vx) * 1.04;
            // Where you hit the paddle sets the angle, which is the whole game.
            ball.vy += (ball.y - you) * 1.6;
            rally++;
            rallyValue.textContent = String(rally);
            tone(520, 40, 'triangle');
          } else {
            theirs++;
            aiValue.textContent = String(theirs);
            tone(160, 220, 'square');
            ctx.submit(rally);
            serve(1);
          }
        }
        if (ball.x > 0.96 && ball.vx > 0) {
          if (Math.abs(ball.y - them) < halfPaddle) {
            ball.vx = -Math.abs(ball.vx) * 1.04;
            ball.vy += (ball.y - them) * 1.4;
            rally++;
            rallyValue.textContent = String(rally);
            tone(440, 40, 'triangle');
          } else {
            mine++;
            scoreValue.textContent = String(mine);
            tone(760, 90, 'triangle');
            serve(-1);
          }
        }
      }

      g.fillStyle = '#05030b';
      g.fillRect(0, 0, W, H);
      g.strokeStyle = 'rgba(139,92,246,0.35)';
      g.setLineDash([8, 12]);
      g.beginPath();
      g.moveTo(W / 2, 0);
      g.lineTo(W / 2, H);
      g.stroke();
      g.setLineDash([]);

      const ph = PADDLE_H * H;
      g.fillStyle = '#22d3ee';
      g.fillRect(W * 0.02, you * H - ph / 2, 8, ph);
      g.fillStyle = '#ff2d95';
      g.fillRect(W * 0.98 - 8, them * H - ph / 2, 8, ph);

      if (running) {
        g.fillStyle = '#facc15';
        g.beginPath();
        g.arc(ball.x * W, ball.y * H, 7, 0, Math.PI * 2);
        g.fill();
      } else {
        g.fillStyle = '#a394c8';
        g.textAlign = 'center';
        g.font = '600 18px system-ui, sans-serif';
        g.fillText('Touche pour commencer', W / 2, H / 2);
      }
    });

    root.append(hud, canvas, h('p', 'ac-note', 'L’angle depend de l’endroit ou la balle touche ta raquette.'));
    return () => life.stop();
  },
};

// -------------------------------------------------------------------- flappy

export const flappy: GameDef = {
  id: 'flappy-bird',
  title: 'Flappy',
  blurb: 'Passe entre les tuyaux sans toucher.',
  category: 'Arcade',
  difficulty: 'Difficile',
  icon: '\u{1F426}',
  unit: 'tuyaux',
  higherIsBetter: true,
  how: 'Clique, touche l’ecran ou appuie sur espace pour battre des ailes. Un tuyau franchi vaut un point.',
  mount(root, ctx) {
    const life = new Lifecycle();
    const [scoreBox, scoreValue] = pill('Score', '0');
    const hud = h('div', 'ac-hud');
    hud.append(scoreBox);

    const canvas = h('canvas', 'ac-canvas wide') as HTMLCanvasElement;
    const { w, h: hh, ctx: g } = fitCanvas(canvas, life);

    // Physics in fractions of the play area, so the difficulty does not depend
    // on the size of the window.
    /*
     * Difficulty is the size of the gap and how fast it comes at you.
     *
     * Gravity and the flap are left alone at every level. Changing those
     * changes how the bird HANDLES, and a game whose controls feel different
     * per difficulty is three games rather than one with a difficulty.
     */
    const GRAVITY = 1.9;
    const FLAP = -0.62;
    const GAP = byLevel(ctx.level, 0.38, 0.28, 0.22);
    const SPEED = byLevel(ctx.level, 0.33, 0.42, 0.52);

    let y = 0.5;
    let vy = 0;
    let pipes: Array<{ x: number; gapY: number; passed: boolean }> = [];
    let score = 0;
    let running = false;
    let dead = false;

    const reset = (): void => {
      y = 0.5;
      vy = 0;
      pipes = [{ x: 1.2, gapY: 0.5, passed: false }];
      score = 0;
      scoreValue.textContent = '0';
      dead = false;
      running = true;
    };

    const flap = (): void => {
      if (dead || !running) {
        reset();
        return;
      }
      vy = FLAP;
      tone(620, 40, 'triangle', 0.035);
    };

    life.on(canvas, 'pointerdown', (ev: Event) => {
      ev.preventDefault();
      flap();
    });
    life.on(window, 'keydown', (ev: KeyboardEvent) => {
      if (ev.key === ' ' || ev.key === 'ArrowUp') {
        ev.preventDefault();
        flap();
      }
    });

    life.frame((dt) => {
      const W = w();
      const H = hh();

      if (running && !dead) {
        vy += GRAVITY * dt;
        y += vy * dt;

        for (const p of pipes) p.x -= SPEED * dt;
        if (pipes.length === 0 || pipes[pipes.length - 1].x < 0.62) {
          pipes.push({ x: 1.15, gapY: 0.22 + Math.random() * 0.56, passed: false });
        }
        pipes = pipes.filter((p) => p.x > -0.2);

        const birdX = 0.24;
        for (const p of pipes) {
          if (!p.passed && p.x + 0.06 < birdX) {
            p.passed = true;
            score++;
            scoreValue.textContent = String(score);
            tone(820, 55, 'triangle');
          }
          const withinX = birdX + 0.035 > p.x - 0.06 && birdX - 0.035 < p.x + 0.06;
          const outsideGap = y < p.gapY - GAP / 2 || y > p.gapY + GAP / 2;
          if (withinX && outsideGap) dead = true;
        }
        if (y > 0.98 || y < 0.02) dead = true;

        if (dead) {
          tone(150, 280, 'square');
          ctx.submit(score);
        }
      }

      g.fillStyle = '#05030b';
      g.fillRect(0, 0, W, H);

      for (const p of pipes) {
        g.fillStyle = 'rgba(163,230,53,0.75)';
        const px = p.x * W;
        const pw = 0.12 * W;
        g.fillRect(px - pw / 2, 0, pw, (p.gapY - GAP / 2) * H);
        g.fillRect(px - pw / 2, (p.gapY + GAP / 2) * H, pw, H);
        g.strokeStyle = '#a3e635';
        g.lineWidth = 2;
        g.strokeRect(px - pw / 2, 0, pw, (p.gapY - GAP / 2) * H);
        g.strokeRect(px - pw / 2, (p.gapY + GAP / 2) * H, pw, H);
      }

      g.fillStyle = '#facc15';
      g.beginPath();
      g.arc(0.24 * W, y * H, Math.min(W, H) * 0.035, 0, Math.PI * 2);
      g.fill();

      if (!running || dead) {
        g.fillStyle = 'rgba(5,3,11,0.72)';
        g.fillRect(0, 0, W, H);
        g.fillStyle = '#f4f0ff';
        g.textAlign = 'center';
        g.font = '800 28px system-ui, sans-serif';
        g.fillText(dead ? `${score} tuyaux` : 'Flappy', W / 2, H / 2 - 6);
        g.font = '500 15px system-ui, sans-serif';
        g.fillStyle = '#a394c8';
        g.fillText('Touche ou espace pour voler', W / 2, H / 2 + 22);
      }
    });

    root.append(hud, canvas, h('p', 'ac-note', 'Les tuyaux ne s’accelerent pas, c’est deja bien assez.'));
    return () => life.stop();
  },
};
